/**
 * The development and QA override of the v2 rollout gates (NEW_ORDER_V2_PLAN.md N10, §13). The
 * allowlists ship empty and no forced-on flag is ever committed, so a developer or a QA device turns
 * a gate on with its own storage key set to "on": in the browser's devtools on web, and with
 * `RolloutOverrides`' switches in mobile's left menu. A gate reads it when its shell mounts, so a
 * change applies after a reload.
 *
 * Only a build that says it is neither production nor beta honours it. An unwired env answers
 * `undefined`, which counts as production: a merchant's device can never turn a gate on.
 *
 * Gates: New Order v2 (`orderTaking/newOrder/config`), the call centre (`operators/callCentre/profile`),
 * My Orders v2 on web (`myOrders/config`; mobile has no v1 list left) and the payment engine on both
 * apps (`paymentEngine/rollout`), the last two added by the user's request of 2026-10-09.
 */
import { getEnv } from "../../effects-bridge";
import { getLocalStorage, removeLocalStorage, setLocalStorage } from "../localStorage";

/** one storage key per gate */
export const ROLLOUT_OVERRIDES = [
  "newOrderV2Override",
  "callCentreOverride",
  "myOrdersV2Override",
  "paymentEngineOverride",
] as const;
export type RolloutOverride = (typeof ROLLOUT_OVERRIDES)[number];

/** where the keys live: the device's storage, or a test's own (`registerOverrideStore`) */
export interface OverrideStore {
  get(key: string): unknown;
  set(key: string, value: string): void;
  remove(key: string): void;
}

const deviceStore: OverrideStore = { get: getLocalStorage, set: setLocalStorage, remove: removeLocalStorage };
let store = deviceStore;

/**
 * For tests: suites run in parallel processes that share one storage file, so each gives the
 * override a store of its own. Returns the restore.
 */
export function registerOverrideStore(next: OverrideStore): () => void {
  store = next;
  return () => {
    store = deviceStore;
  };
}

export function overridesAllowed(): boolean {
  return getEnv("isBetaOrProdEnv") === false;
}

export function devOverride(key: RolloutOverride): boolean {
  return overridesAllowed() && store.get(key) === "on";
}

/** does nothing in a production or beta build */
export function setDevOverride(key: RolloutOverride, on: boolean): void {
  if (!overridesAllowed()) return;
  if (on) store.set(key, "on");
  else store.remove(key);
}

/** an in-memory store, for tests */
export function memoryOverrideStore(): OverrideStore {
  const values = new Map<string, string>();
  return { get: (key) => values.get(key), set: (key, value) => void values.set(key, value), remove: (key) => void values.delete(key) };
}
